/**
 * searchAlphaSense Tool - Factory
 */
import { ToolBase, ToolDescription } from "../../../agents/base";
import GlobalStateManager from "../../../global-state/globalStateManager";
import { SearchAlphaSenseLegacyParamsSchema, SearchAlphaSenseParamsSchema, } from "./searchAlphaSense.schema";
import { executeAlphaSenseSearch, } from "./searchAlphaSense.shared";
const SYSTEM_PROMPT_DESCRIPTION = `### searchAlphaSense
Search AlphaSense for financial research, earnings calls, SEC filings, and broker reports.

Use this tool when you need:
- Company financial metrics, trends, and guidance
- Earnings transcript excerpts and management commentary
- Broker research and market intelligence
- SEC filing evidence

Batch queries execute in parallel. Prefer one call with all needed queries:
\`\`\`json
{
  "queries": [
    { "query": "Apple Q4 2025 iPhone revenue", "use_findata_agent": true },
    { "query": "Apple management commentary on AI strategy", "use_findata_agent": false }
  ]
}
\`\`\`

Returns chunks with \`contextId\` (for example, \`as_x7f9k2\`) for compact follow-up references.`;
function asObject(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function unwrapRequest(raw) {
    const obj = asObject(raw);
    const nested = obj.request;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        return nested;
    }
    return obj;
}
export function normalizeSearchAlphaSenseQueries(raw) {
    const request = unwrapRequest(raw);
    const parsedBatch = SearchAlphaSenseParamsSchema.safeParse(request);
    if (parsedBatch.success) {
        return parsedBatch.data.queries;
    }
    const parsedLegacy = SearchAlphaSenseLegacyParamsSchema.safeParse(request);
    if (parsedLegacy.success) {
        return [
            {
                query: parsedLegacy.data.query,
                use_findata_agent: parsedLegacy.data.use_findata_agent ?? false,
            },
        ];
    }
    throw new Error(`Invalid searchAlphaSense parameters: ${parsedBatch.error.message}; ${parsedLegacy.error.message}`);
}
export function createSearchAlphaSenseTool(options = {}) {
    return new SearchAlphaSenseTool(options).toRuntimeToolDefinition();
}
export class SearchAlphaSenseTool extends ToolBase {
    constructor(options = {}) {
        super();
        this.options = options;
        this.name = "searchAlphaSense";
        this.description = new ToolDescription("searchAlphaSense", "Search AlphaSense for financial research and return chunked context IDs.", SearchAlphaSenseParamsSchema);
        this.paramsSchema = SearchAlphaSenseParamsSchema;
        this.systemPromptDescription = SYSTEM_PROMPT_DESCRIPTION;
    }
    storeChunksInContext(response) {
        const globalState = GlobalStateManager.getInstance();
        for (const queryResult of response.queryResults) {
            for (const chunk of queryResult.chunks) {
                globalState.addOrUpdateContext(chunk.contextId, {
                    id: chunk.contextId,
                    content: chunk.content,
                    query: queryResult.query,
                    index: chunk.index,
                    citations: chunk.citations,
                    source: "alphasense",
                    createdAt: new Date().toISOString(),
                });
            }
        }
    }
    async execute(args) {
        const queries = normalizeSearchAlphaSenseQueries(args);
        const response = await executeAlphaSenseSearch(queries, this.options);
        this.storeChunksInContext(response);
        return response;
    }
}
